/**
 * Website Performance — Scroll depth, click tracking, booking funnel, and heatmap insights
 * Data: GA4 live hooks (scroll depth, top clicks, funnel, pages)
 */

import React, { useState, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { ChevronRight, ExternalLink, MousePointerClick, ScrollText, ArrowDownRight, Target } from 'lucide-react';
import { motion } from 'framer-motion';
import { staggerContainer, fadeInUp, pageTransition, pageInitial, pageAnimate } from '@/lib/motionVariants';
import { formatNumber, formatPercentRaw } from '@/lib/formatting';
import { TEXT, BORDER, BRAND, CHART_COLORS, getChartTooltip } from '../charts/utils';
import { useChatStore } from '@/stores/chatStore';
import { useThemeKey } from '@/hooks/useThemeKey';
import { useScrollDepth, useTopClicks, useFunnel, usePages } from '@/hooks/useGA4Data';
import PageHeader from '../shared/PageHeader';
import ScrollDepthPanel from '../drill/ScrollDepthPanel';
import type { GA4ScrollDepthEntry } from '@/types/dashboard';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

/* ============================================================================
   Chat Drill-Down Helper
   ============================================================================ */

const askChat = (query: string, e?: React.MouseEvent) => {
  if (e) {
    useChatStore.getState().showAnalyzePrompt(
      query, '', (e.currentTarget as HTMLElement).getBoundingClientRect(),
    );
  } else {
    useChatStore.getState().sendAnalyzeQuery(query);
  }
};

/* ============================================================================
   Skeleton Components
   ============================================================================ */

function SkeletonCard() {
  return (
    <div className="bg-bg-1 border border-border rounded-xl p-4 animate-pulse">
      <div className="h-3 w-20 bg-bg-2 rounded mb-2" />
      <div className="h-6 w-16 bg-bg-2 rounded" />
    </div>
  );
}

function SkeletonChart() {
  return (
    <div className="bg-bg-1 border border-border rounded-xl p-6 animate-pulse">
      <div className="h-4 w-32 bg-bg-2 rounded mb-4" />
      <div className="h-[300px] bg-bg-2 rounded" />
    </div>
  );
}

/* ============================================================================
   Component
   ============================================================================ */

const WebsitePerformance: React.FC = () => {
  const themeKey = useThemeKey();
  const chartTooltip = getChartTooltip();
  const { data: scrollData, source: scrollSource, note: scrollNote, loading: scrollLoading } = useScrollDepth();
  const { data: clickData, loading: clicksLoading } = useTopClicks();
  const { data: funnelData, loading: funnelLoading } = useFunnel();
  const { data: pageData, loading: pagesLoading } = usePages();

  // Drill panel state for scroll depth
  const [selectedScroll, setSelectedScroll] = useState<GA4ScrollDepthEntry | null>(null);
  const [drillOpen, setDrillOpen] = useState(false);

  // Derived KPIs
  const anyLoading = scrollLoading || clicksLoading || funnelLoading || pagesLoading;
  const totalPageViews = pageData ? pageData.reduce((s, p) => s + p.views, 0) : 0;
  const scrollEvents = scrollData
    ? scrollData.reduce((s, entry) => s + Object.values(entry.depths).reduce((a, b) => a + b, 0), 0)
    : 0;
  const totalClicks = clickData ? clickData.reduce((s, c) => s + c.count, 0) : 0;
  const ctaClicks = clickData ? clickData.filter(c => !c.outbound).reduce((s, c) => s + c.count, 0) : 0;

  // Scroll depth chart — horizontal bar, one bar per page (sum of all depths)
  const scrollBarData = useMemo(() => {
    if (!scrollData) return null;
    const sorted = [...scrollData].sort(
      (a, b) => Object.values(b.depths).reduce((s, v) => s + v, 0) - Object.values(a.depths).reduce((s, v) => s + v, 0),
    );
    const top = sorted.slice(0, 8);
    return {
      labels: top.map(e => e.page),
      datasets: [{
        label: 'Scroll Events',
        data: top.map(e => Object.values(e.depths).reduce((s, v) => s + v, 0)),
        backgroundColor: top.map((_, i) => i === 0 ? BRAND.gold : CHART_COLORS[2]),
        borderRadius: 4,
      }],
    };
  }, [scrollData]);

  // Funnel chart — horizontal bar
  const funnelBarData = useMemo(() => {
    if (!funnelData) return null;
    return {
      labels: funnelData.map(s => s.stage),
      datasets: [{
        label: 'Users',
        data: funnelData.map(s => s.value),
        backgroundColor: funnelData.map((_, i) => i === 0 ? CHART_COLORS[2] : i === funnelData.length - 1 ? BRAND.gold : CHART_COLORS[1]),
        borderRadius: 4,
      }],
    };
  }, [funnelData]);

  // Funnel drop-off rates
  const funnelDropOffs = useMemo(() => {
    if (!funnelData || funnelData.length < 2) return [];
    return funnelData.slice(1).map((stage, idx) => {
      const prev = funnelData[idx];
      const dropOff = prev.value > 0 ? Math.round(((prev.value - stage.value) / prev.value) * 100) : 0;
      return {
        from: prev.stage,
        to: stage.stage,
        dropOff,
      };
    });
  }, [funnelData]);

  return (
    <motion.div className="space-y-6" initial={pageInitial} animate={pageAnimate} transition={pageTransition}>
      <PageHeader title="Website Performance" subtitle="Scroll depth, click tracking, booking flow, and heatmap insights" />

      {/* 1. KPI Strip */}
      <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-3" variants={staggerContainer} initial="hidden" animate="visible">
        {anyLoading ? (
          <>
            <motion.div variants={fadeInUp}><SkeletonCard /></motion.div>
            <motion.div variants={fadeInUp}><SkeletonCard /></motion.div>
            <motion.div variants={fadeInUp}><SkeletonCard /></motion.div>
            <motion.div variants={fadeInUp}><SkeletonCard /></motion.div>
          </>
        ) : (
          <>
            <motion.div variants={fadeInUp}>
              <div className="bg-bg-1 border border-border rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <ScrollText className="w-3.5 h-3.5 text-text-3" />
                  <p className="text-xs text-text-3 uppercase tracking-wider font-semibold">Page Views</p>
                </div>
                <p className="font-mono text-lg font-bold text-text">{formatNumber(totalPageViews)}</p>
              </div>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <div className="bg-bg-1 border border-border rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <ArrowDownRight className="w-3.5 h-3.5 text-text-3" />
                  <p className="text-xs text-text-3 uppercase tracking-wider font-semibold">Scroll Events</p>
                </div>
                <p className="font-mono text-lg font-bold text-text">{formatNumber(scrollEvents)}</p>
              </div>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <div className="bg-bg-1 border border-border rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <MousePointerClick className="w-3.5 h-3.5 text-text-3" />
                  <p className="text-xs text-text-3 uppercase tracking-wider font-semibold">Total Clicks</p>
                </div>
                <p className="font-mono text-lg font-bold text-text">{formatNumber(totalClicks)}</p>
              </div>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <div className="bg-bg-1 border border-monarch-gold/40 rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <Target className="w-3.5 h-3.5 text-monarch-gold" />
                  <p className="text-xs text-text-3 uppercase tracking-wider font-semibold">CTA Clicks</p>
                </div>
                <p className="font-mono text-lg font-bold text-monarch-gold">{formatNumber(ctaClicks)}</p>
              </div>
            </motion.div>
          </>
        )}
      </motion.div>

      {/* 2. Charts Row: Scroll Depth + Booking Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {scrollLoading || !scrollBarData ? (
          <SkeletonChart />
        ) : (
          <div className="bg-bg-1 border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-sm text-text-2">Scroll Depth by Page</h3>
              {scrollSource && (
                <span className="text-[10px] text-text-3 bg-bg-2 px-2 py-0.5 rounded-full">{scrollSource}</span>
              )}
            </div>
            {scrollNote && (
              <p className="text-xs text-text-3 mb-3 italic">{scrollNote}</p>
            )}
            <div style={{ height: 300 }}>
              <Bar
                key={`scroll-bar-${themeKey}`}
                data={scrollBarData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: 'y',
                  onClick: (_event, elements) => {
                    if (elements.length > 0 && scrollData) {
                      const idx = elements[0].index;
                      const sorted = [...scrollData].sort(
                        (a, b) => Object.values(b.depths).reduce((s, v) => s + v, 0) - Object.values(a.depths).reduce((s, v) => s + v, 0),
                      );
                      const entry = sorted[idx];
                      if (entry) {
                        setSelectedScroll(entry);
                        setDrillOpen(true);
                      }
                    }
                  },
                  scales: {
                    x: { grid: { color: BORDER.default }, ticks: { color: TEXT.faint, font: { size: 10 }, callback: (v) => formatNumber(Number(v)) }, border: { display: false } },
                    y: { grid: { display: false }, ticks: { color: TEXT.muted, font: { size: 11 } }, border: { display: false } },
                  },
                  plugins: { legend: { display: false }, tooltip: chartTooltip },
                }}
              />
            </div>
          </div>
        )}

        {funnelLoading || !funnelBarData ? (
          <SkeletonChart />
        ) : (
          <div className="bg-bg-1 border border-border rounded-xl p-6">
            <h3 className="font-display text-sm text-text-2 mb-4">Booking Conversion Funnel</h3>
            <div style={{ height: 300 }}>
              <Bar
                key={`funnel-bar-${themeKey}`}
                data={funnelBarData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: 'y',
                  scales: {
                    x: { grid: { color: BORDER.default }, ticks: { color: TEXT.faint, font: { size: 10 }, callback: (v) => formatNumber(Number(v)) }, border: { display: false } },
                    y: { grid: { display: false }, ticks: { color: TEXT.muted, font: { size: 11 } }, border: { display: false } },
                  },
                  plugins: { legend: { display: false }, tooltip: chartTooltip },
                }}
              />
            </div>
            {funnelDropOffs.length > 0 && (
              <div className="mt-4 space-y-1.5">
                {funnelDropOffs.map((d) => (
                  <div key={`${d.from}-${d.to}`} className="flex items-center justify-between text-xs">
                    <span className="text-text-3">{d.from} &rarr; {d.to}</span>
                    <span className={`font-mono font-semibold ${d.dropOff > 50 ? 'text-danger' : d.dropOff > 25 ? 'text-warning' : 'text-success'}`}>
                      -{d.dropOff}% drop-off
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 3. Most Clicked Elements Table */}
      <div className="bg-bg-1 border border-border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="font-display text-sm text-text-2">Most Clicked Elements</h3>
        </div>
        {clicksLoading ? (
          <div className="p-6 animate-pulse">
            <div className="h-4 w-full bg-bg-2 rounded mb-3" />
            <div className="h-4 w-full bg-bg-2 rounded mb-3" />
            <div className="h-4 w-3/4 bg-bg-2 rounded" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-bg-2 text-text-3">
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Link Text</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">URL</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Clicks</th>
                  <th className="px-4 py-3 text-center font-semibold uppercase tracking-wider">Type</th>
                </tr>
              </thead>
              <tbody>
                {(clickData || []).slice(0, 15).map((row, idx) => (
                  <tr key={idx} className="border-b border-border/50 hover:bg-bg-2 transition-colors">
                    <td className="px-4 py-3 font-medium text-text max-w-[200px] truncate">{row.text || '(no text)'}</td>
                    <td className="px-4 py-3 text-text-3 max-w-[280px] truncate font-mono text-[10px]">{row.url}</td>
                    <td className="px-4 py-3 text-right font-mono text-text">{formatNumber(row.count)}</td>
                    <td className="px-4 py-3 text-center">
                      {row.outbound ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 text-[10px] font-semibold">
                          <ExternalLink className="w-3 h-3" /> Outbound
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-monarch-gold/10 text-monarch-gold text-[10px] font-semibold">
                          Internal
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 4. Bottom Row: Clarity Card + AI Insight Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <a
          href="https://clarity.microsoft.com"
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-gradient-to-br from-purple-600/10 to-purple-800/5 border border-purple-500/30 rounded-xl p-5 hover:brightness-110 transition-all group"
        >
          <div className="flex items-center gap-2 mb-2">
            <MousePointerClick className="w-5 h-5 text-purple-400" />
            <p className="text-sm font-semibold text-text">Microsoft Clarity</p>
            <ExternalLink className="w-3.5 h-3.5 text-text-3 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
          </div>
          <p className="text-xs text-text-3 leading-relaxed">
            View session recordings, heatmaps, and rage click analysis. Clarity provides a free layer of behavioral analytics that complements GA4 scroll and click data with visual evidence.
          </p>
        </a>
        <div
          className="bg-monarch-gold/5 border border-monarch-gold/30 rounded-xl p-5 cursor-pointer hover:brightness-110 transition-all"
          onClick={(e) => askChat(
            'Analyze the website performance data: scroll depth drop-offs, click patterns, and booking funnel conversion rates. What are the biggest opportunities to improve conversion?',
            e,
          )}
        >
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-monarch-gold" />
            <p className="text-sm font-semibold text-text">AI Performance Insight</p>
            <ChevronRight className="w-3.5 h-3.5 text-text-3 ml-auto" />
          </div>
          <p className="text-xs text-text-3 leading-relaxed">
            Click to ask AI about scroll depth patterns, click distribution, and funnel drop-offs. Get actionable recommendations for improving booking conversion rates and user engagement.
          </p>
        </div>
      </div>

      {/* 5. Scroll Depth Drill Panel */}
      <ScrollDepthPanel entry={selectedScroll} open={drillOpen} onClose={() => { setDrillOpen(false); setSelectedScroll(null); }} />
    </motion.div>
  );
};

export default WebsitePerformance;
